import React, { useEffect, useState, useMemo } from "react"
import { Table, Row, Col, Container, Modal, Form, Label, Card, CardBody } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { useTable, usePagination, useGlobalFilter } from "react-table"
import Select from "react-select"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import Swal from "sweetalert2"
import NewPagination from "components/Common/Newpagination"
import Breadcrumbs from "components/Common/Breadcrumb"
import Loader from "pages/Separate/Loader"
import { fetchNodeData, FetchNodeDeleteData, FetchNodePostData, FetchNodePutData } from "utils/common-fetchData"
import secureLocalStorage from "react-secure-storage"

const EMPTY_FORM = {
  department: null, // react-select object { value, label }
  designation: "",
  roles: "",
  kra: "",
}

// Add / Edit Modal
function PositionModal({ isOpen, onClose, form, setForm, onSubmit, isEdit, departmentOptions }) {
  const handleField = (name, value) =>
    setForm(prev => ({ ...prev, [name]: value }))

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <Form onSubmit={onSubmit}>

        <div className="modal-header">
          <h5 className="modal-title mt-0">{isEdit ? "Edit" : "Add"} Position</h5>
          <div>
            <button type="submit" className="border-radius btn btn-purple me-4">
              <i className="fas fa-save me-2"></i>
              {isEdit ? "Update Position" : "Add Position"}
            </button>
            <button type="button" onClick={onClose} className="btn-modal-close">
              <span className="btn-modal-span">&times;</span>
            </button>
          </div>
        </div>

        <div className="modal-body">
          <Row>
            {/* Department */}
            <Col md={6} className="mb-3">
              <Label>
                Department <span className="text-danger">*</span>
              </Label>
              <Select
                placeholder="Select Department"
                className="basic-multi-select"
                classNamePrefix="select"
                options={departmentOptions}
                value={form.department}
                onChange={e => handleField("department", e)}
              />
              {/* hidden input so required validation works inside <Form> */}
              <input
                tabIndex={-1}
                style={{ opacity: 0, height: 0, position: "absolute" }}
                value={form.department?.value || ""}
                onChange={() => { }}
                required
              />
            </Col>

            {/* Designation */}
            <Col md={6} className="mb-3">
              <Label>
                Designation <span className="text-danger">*</span>
              </Label>
              <input
                type="text"
                name="designation"
                className="form-control"
                placeholder="e.g. Frontend Developer"
                value={form.designation}
                onChange={e => handleField("designation", e.target.value)}
                required
              />
            </Col>

            {/* Roles & Responsibilities */}
            <Col md={12} className="mb-3">
              <Label>
                Roles & Responsibilities <span className="text-danger">*</span>
              </Label>
              <CKEditor
                editor={ClassicEditor}
                data={form.roles}
                onChange={(_event, editor) =>
                  handleField("roles", editor.getData())
                }
              />
              {/* <small className="text-muted">
                Will be auto-inserted into the appointment letter.
              </small> */}
            </Col>

            {/* KRA */}
            <Col md={12} className="mb-3">
              <Label>
                KRA – Key Result Areas <span className="text-danger">*</span>
              </Label>
              <CKEditor
                editor={ClassicEditor}
                data={form.kra}
                onChange={(_event, editor) =>
                  handleField("kra", editor.getData())
                }
              />
              {/* <small className="text-muted">
                Will be auto-inserted into the appointment letter.
              </small> */}
            </Col>
          </Row>

          <div className="mt-2 d-flex gap-2">
            <button type="submit" className="border-radius btn btn-purple">
              <i className="fas fa-save me-2"></i>
              {isEdit ? "Update Position" : "Add Position"}
            </button>
          </div>
        </div>
      </Form>
    </Modal>
  )
}

// Table Container
function TableContainer({ columns, data, onAdd }) {
  const [fixedContent, setFixedContent] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const handleScroll = () => setFixedContent(window.scrollY > 160)
    document.addEventListener("scroll", handleScroll)
    return () => document.removeEventListener("scroll", handleScroll)
  }, [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
    prepareRow,
  } = useTable({ columns, data }, useGlobalFilter, usePagination)

  const { pageIndex, pageSize, globalFilter } = state

  return (
    <div>
      {/* Floating buttons */}
      <div
        className={`flex-column fixed-buttons ${fixedContent ? "d-flex" : "d-none"
          }`}
      >
        <button
          onClick={onAdd}
          className="btn btn-purple border-radius fix-btn"
        >
          <i className="fas fa-plus me-2"></i>
          <span>Position</span>
        </button>
        <button
          onClick={() => navigate(-1)}
          className="btn btn-red border-radius fix-btn"
        >
          <i className="dripicons-cross me-2 icon-center"></i>
          <span>Cancel</span>
        </button>
      </div>

      {/* Toolbar */}
      <Row className="mb-2 align-items-center">
        <Col lg={2}>
          <select
            className="border-radius form-select"
            style={{ width: "100px" }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 40, 50].map(s => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </Col>

        <Col className="d-flex justify-content-end gap-3 align-items-center">
          <div className="search-box d-inline-block">
            <div className="position-relative">
              <input
                type="text"
                className="border-radius form-control rounded"
                placeholder="Search..."
                value={globalFilter || ""}
                onChange={e => setGlobalFilter(e.target.value)}
              />
              <i className="bx bx-search-alt search-icon"></i>
            </div>
          </div>
          <button className="border-radius btn btn-purple" onClick={onAdd}>
            <i className="fas fa-plus me-2 icon-size"></i>Position
          </button>
          <button
            className="border-radius btn btn-red text-white"
            onClick={() => navigate(-1)}
          >
            <i className="dripicons-cross me-2 icon-center"></i>Cancel
          </button>
        </Col>
      </Row>

      {/* Table */}
      <Table className="table table-bordered mt-4" {...getTableProps()}>
        <thead className={fixedContent ? "fixed-setting-header" : ""}>
          {headerGroups.map((hg, i) => (
            <tr key={i} {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col, j) => (
                <th key={j} className={col.className} {...col.getHeaderProps()}>
                  {col.render("Header")}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <tr className="row-hover" key={i} {...row.getRowProps()}>
                {row.cells.map((cell, j) => (
                  <td
                    key={j}
                    className={cell.column.className}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            )
          })}
          {page.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className="text-center text-muted py-4"
              >
                No positions found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination */}
      <Row className="justify-content-md-end justify-content-center align-items-center">
        <NewPagination
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          previousPage={previousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          pageSize={pageSize}
          page={page}
          data={data}
          nextPage={nextPage}
        />
      </Row>
    </div>
  )
}

// Main Component
const PositionMaster = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  const [departments, setDepartments] = useState([])

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetchNodeData(setLoading, 'departments', secureLocalStorage.removeItem("auth_token"))
        setDepartments(
          response.data?.department?.map((item) =>
            ({ value: item.id, label: item.department_name })) || []
        )
      } catch (err) {
        console.log(err)
      }
    }
    fetchDepartments()
    document.title = "Position Master | Zithas Crm"
  }, [])

  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const response = await fetchNodeData(setLoading, 'positionMaster/get', secureLocalStorage.getItem("auth_token"))
        setData(response.data.map((item) => ({
          ...item,
          department: departments.find(d => d.value === item.department)
        })) || [])
      } catch (err) {
        console.log(err)
      }
    }
    if (departments.length)
      fetchPositions()
  }, [departments])


  // ── Handlers
  const openAdd = () => {
    setIsEdit(false)
    setEditId(null)
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = row => {
    setIsEdit(true)
    setEditId(row.id)
    setForm({
      department: row.department || null,
      designation: row.designation,
      roles: row.RolesAndResponsibilities,
      kra: row.KeyResultAreas,
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setForm(EMPTY_FORM)
    setEditId(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const token = secureLocalStorage.getItem("auth_token");

    if (!form.department || !form.roles || !form.kra) {
      Swal.fire("Validation", "All fields required.", "warning")
      return
    }

    Swal.fire({
      icon: "warning",
      title: "Are you sure ?",
      text: `You want to ${isEdit ? 'update' : 'add'} position`,
      showCancelButton: true,
    }).then(async willOkay => {
      if (willOkay.isConfirmed) {
        try {
          const entry = {
            department: form.department.value,
            designation: form.designation,
            RolesAndResponsibilities: form.roles,
            KeyResultAreas: form.kra,
          }

          let response;

          if (isEdit) {
            // Update existing
            response = await FetchNodePutData(setLoading, `positionMaster/update`, editId, entry)
            const updated = response.data.data
            setData(prev =>
              prev.map(item => (item.id === editId ? {
                ...updated,
                department: departments.find(d => d.value === updated.department) || {}
              } : item))
            )
            Swal.fire("Updated!", "Position updated successfully.", "success")
          } else {

            // Add new
            response = await FetchNodePostData(setLoading, `positionMaster/add`, token, entry)

            const added = response.data.data

            setData(prev => [{
              ...added,
              department: departments.find(d => d.value === added.department) || {}
            }, ...prev])
            Swal.fire("Added!", "Position added successfully.", "success")
          }

          closeModal()
        } catch (error) {
          console.log(error)
          swal("", "Something went wrong!", "error")
        }
      }
    })


  }

  const handleDelete = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This position will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#762c99",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async result => {
      if (result.isConfirmed) {

        const response = await FetchNodeDeleteData(
          setLoading,
          `positionMaster/delete`,
          id
        );

        if (response.data.success) {
          setData(prev => prev.filter(item => item.id != id))
          Swal.fire("Deleted!", "Position has been deleted.", "success")
        }
      }
    })
  }

  const stripHtml = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        className: "text-center",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Department",
        accessor: "department.label",
        className: "text-center",
      },
      {
        Header: "Designation",
        accessor: "designation",
        className: "text-start",
      },
      {
        Header: "Roles & Responsibilities",
        accessor: "RolesAndResponsibilities",
        className: "text-start",
        Cell: ({ value }) => (
          <div
            className="ckeditor-content"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              maxWidth: 320,
              fontSize: 13,
            }}
          > {stripHtml(value)}</div>
        ),
      },
      {
        Header: "KRA",
        accessor: "KeyResultAreas",
        className: "text-start",
        Cell: ({ value }) => (
          <div
            className="ckeditor-content"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              maxWidth: 260,
              fontSize: 13,
            }}
          >{stripHtml(value)}</div>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        className: "text-center",
        Cell: ({ row }) => (
          <div className="d-flex justify-content-center gap-1">
            <button
              className="btn btn-sm btn-outline-secondary"
              title="Edit"
              onClick={() => openEdit(row.original)}
            >
              <i className="fas fa-pencil-alt"></i>
            </button>
            <button
              className="btn btn-sm btn-danger"
              title="Delete"
              onClick={() => handleDelete(row.original.id)}
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      {loading && <Loader />}

      <div className="page-content mb-4">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Position Master" />

          <Card>
            <CardBody>
              <div className="responsive-table">
                <TableContainer columns={columns} data={data} onAdd={openAdd} />
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      <PositionModal
        isOpen={modalOpen}
        onClose={closeModal}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        departmentOptions={departments}
        isEdit={isEdit}
      />
    </React.Fragment>
  )
}

export default PositionMaster
